import { API_URL } from "./api";

const VISITOR_KEY = "avalon_visitor_id";

/**
 * Anonymous visitor id used only to count unique visitors (same idea as an analytics client id).
 * It is a random UUID with no personal data, so keeping it in localStorage is safe; if storage is
 * unavailable (privacy mode) we fall back to a per-page-load id.
 */
export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}`;
  }
};

export const detectDevice = () => {
  const ua = navigator.userAgent || "";
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua)) return "mobile";
  if (window.innerWidth < 768) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
};

export const getUtmParams = (search = window.location.search) => {
  const p = new URLSearchParams(search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_content: p.get("utm_content") || "",
    utm_term: p.get("utm_term") || "",
  };
};

const cleanReferrer = () => {
  try {
    if (!document.referrer) return "";
    const u = new URL(document.referrer);
    if (u.host === window.location.host) return "";
    return u.host;
  } catch {
    return "";
  }
};

/**
 * Fire-and-forget tracking call. Uses fetch keepalive so click events survive navigation.
 */
export const track = (slug, type, extra = {}) => {
  const payload = {
    slug,
    type,
    visitor_id: getVisitorId(),
    device: detectDevice(),
    referrer: cleanReferrer(),
    path: window.location.pathname + window.location.search,
    ...getUtmParams(),
    ...extra,
  };
  try {
    return fetch(`${API_URL}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((err) => console.warn("[tracking] event not recorded", type, err?.message || err));
  } catch (err) {
    console.warn("[tracking] fetch unavailable", err?.message || err);
    return Promise.resolve();
  }
};

// ---------------------------------------------------------------- Meta Pixel
let pixelLoaded = false;
const initializedPixels = new Set();

/** Loads Meta's fbevents.js once and creates the `fbq` queue shim (equivalent of Meta's base code). */
const ensureFbq = () => {
  if (window.fbq) return;
  const fbq = (...args) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
};

export const loadMetaPixel = (pixelId) => {
  if (!pixelId) return;
  if (!pixelLoaded) {
    ensureFbq();
    pixelLoaded = true;
  }
  if (!initializedPixels.has(pixelId)) {
    window.fbq("init", pixelId);
    initializedPixels.add(pixelId);
  }
  window.fbq("track", "PageView");
};

export const fbqTrack = (eventName, params = {}) => {
  if (typeof window.fbq === "function" && eventName) {
    const standard = ["Lead", "Contact", "Schedule", "ViewContent", "Purchase", "CompleteRegistration", "SubmitApplication", "AddToCart", "InitiateCheckout", "Search", "PageView"];
    if (standard.includes(eventName)) window.fbq("track", eventName, params);
    else window.fbq("trackCustom", eventName, params);
  }
};

/** Injects an arbitrary pasted pixel snippet (scripts execute). Admin-provided only. */
export const injectCustomCode = (code, containerId = "avalon-custom-pixel") => {
  if (!code || document.getElementById(containerId)) return;
  try {
    const holder = document.createElement("div");
    holder.id = containerId;
    holder.style.display = "none";
    const frag = document.createRange().createContextualFragment(code);
    holder.appendChild(frag);
    document.body.appendChild(holder);
  } catch (err) {
    console.warn("[tracking] custom pixel snippet could not be injected", err?.message || err);
  }
};

// ---------------------------------------------------------------- Google (GA4 + Google Ads via gtag.js)
let gtagLoaded = false;
const configuredGtagIds = new Set();

export const loadGtag = (ids = []) => {
  const list = ids.filter(Boolean).map((s) => s.trim());
  if (!list.length) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // gtag.js expects an `arguments` object on the dataLayer, so keep the classic function form.
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  if (!gtagLoaded) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(list[0])}`;
    document.head.appendChild(s);
    window.gtag("js", new Date());
    gtagLoaded = true;
  }
  list.forEach((id) => {
    if (!configuredGtagIds.has(id)) {
      window.gtag("config", id);
      configuredGtagIds.add(id);
    }
  });
};

export const gtagEvent = (name, params = {}) => {
  if (typeof window.gtag === "function" && name) window.gtag("event", name, params);
};

/** Google Ads conversion: send_to = "AW-XXXX/label" */
export const gtagConversion = (adsId, label, params = {}) => {
  if (typeof window.gtag !== "function" || !adsId) return;
  const sendTo = label ? `${adsId}/${label}` : adsId;
  window.gtag("event", "conversion", { send_to: sendTo, ...params });
};

// ---------------------------------------------------------------- TikTok Pixel
const loadedTtqIds = new Set();

const TTQ_METHODS = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];

/** Creates the `ttq` queue shim (equivalent of TikTok's base code) once. */
const ensureTtq = () => {
  if (window.ttq) return window.ttq;
  window.TiktokAnalyticsObject = "ttq";
  const ttq = [];
  ttq.methods = TTQ_METHODS;
  ttq.setAndDefer = (target, method) => {
    target[method] = (...args) => target.push([method, ...args]);
  };
  TTQ_METHODS.forEach((m) => ttq.setAndDefer(ttq, m));
  ttq.instance = (id) => {
    const inst = ttq._i?.[id] || [];
    TTQ_METHODS.forEach((m) => ttq.setAndDefer(inst, m));
    return inst;
  };
  ttq.load = (id, options) => {
    const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = src;
    ttq._t = ttq._t || {};
    ttq._t[id] = Date.now();
    ttq._o = ttq._o || {};
    ttq._o[id] = options || {};
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `${src}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
    document.head.appendChild(script);
  };
  window.ttq = ttq;
  return ttq;
};

export const loadTikTok = (pixelId) => {
  if (!pixelId) return;
  const ttq = ensureTtq();
  if (!loadedTtqIds.has(pixelId)) {
    ttq.load(pixelId);
    loadedTtqIds.add(pixelId);
  }
  ttq.page();
};

export const ttqTrack = (eventName, params = {}) => {
  if (window.ttq && typeof window.ttq.track === "function" && eventName) window.ttq.track(eventName, params);
};

/** Returns a list of enabled platform labels for a pixel config (used in the admin UI). */
export const activePixelPlatforms = (pixel = {}) => {
  const out = [];
  if (pixel.enabled && (pixel.meta_pixel_id || pixel.custom_code)) out.push("Meta");
  if (pixel.ga4_enabled && pixel.ga4_measurement_id) out.push("GA4");
  if (pixel.google_ads_enabled && pixel.google_ads_id) out.push("Google Ads");
  if (pixel.tiktok_enabled && pixel.tiktok_pixel_id) out.push("TikTok");
  return out;
};
